import dayjs from 'dayjs';

import { IFBCustomFormFieldResponse, IFBCustomFormResponse } from '@app/models/custom-form';
import { IFBMFADevice } from '@app/models/security';
import { IFBRegistrationLinkSource } from '@app/models/registration';
import { IFBConsentForm, IFBContentFormFieldResponse } from '@app/models/consent-form';

export enum EFBUserRole {
  COMPANY_ADMIN = 'ROLE_COMPANY_ADMIN',
  RECRUITER = 'ROLE_RECRUITER',
  TALENT_SCOUT = 'ROLE_TALENT_SCOUT'
}

export interface IFBApiErrorResponse {
  config: object;
  data: {
    code: string;
    developerMessage?: string;
    errors?: string[];
    message: string;
    status: number;
  };
  headers: (name: string) => string;
  status: number;
  statusText: string;
}

export interface IFBDataPage {
  items: any[];
  first: { href: string };
  href: string;
  last: { href: string };
  limit: number;
  next: { href: string };
  offset: number;
  total: number;
}

export interface IFBUsersPage extends IFBDataPage {
  items: IFBUserInfo[];
}

export interface IFBTenantProperty {
  propertyId: string;
  valueId: string;
}

export interface IFBUserInfo {
  achievementAccessToken?: string;
  companyId?: string;
  createdAt?: string;
  department?: IFBDepartment;
  email?: string;
  employeeId?: string;
  firstName?: string;
  href?: string;
  id?: string;
  incognito?: boolean;
  lastName?: string;
  locale: string;
  location?: IFBLocation;
  mainRole?: EFBUserRole;
  meta?: { provisioning: { provisionedFields: string[] } };
  mfaDevice?: IFBMFADevice;
  mfaEnabled?: boolean;
  mfaToken?: string;
  notificationAccessToken?: string;
  otpToken?: string;
  password?: string;
  profile_picture?: { href: string; id: string };
  profilePicture?: { href?: string; id?: string }; // Due to BE inconsistency we need to properly fill profilePicture
  properties?: IFBTenantProperty[];
  role?: EFBUserRole;
  roles: EFBUserRole[];
  status?: string;
  timelineAccessToken?: string;
  timeZone: string;
  unsaved?: boolean;
}

export interface IFBCompanyLogo {
  id: string;
  href: string;
}

export interface IFBLocaleOverride {
  originalLocale: string;
  replacingLocale: string;
}

export interface IFBCompanyFeatureFlags {
  [key: string]: boolean;
}

export interface IFBCompanyInvitation {
  email: string;
  id: string;
  invitationDate: string;
  role: EFBUserRole;
}

export interface IFBCompanyInvitationsPage extends IFBDataPage {
  items: IFBCompanyInvitation[];
  total: number;
}

export interface IFBMetadataInfo<T> {
  id: string;
  components: T[];
  defaultLocale: string;
  domainName: string;
  cname?: string;
  enabledLocales: [];
  localeOverrides: [];
  settings: IFBCompanySettings;
}

export interface IFBMetadataComponent {
  name: string;
  status: 'ENABLED' | 'DISABLED';
}

export interface IFBCompanySettings {
  hideRecruiterDetails: boolean;
  disableMultiJobsShare: boolean;
}

export interface IFBCompanySettingsUpdateResponse {
  settings: IFBCompanySettings;
}

export interface IFBPlan {
  apiKey: boolean;
  ciCustomizationEnabled: boolean;
  code: string;
  companyJobApplicationEmailReceiver: boolean;
  companyLogoEnabled: boolean;
  hiringTeam: boolean;
  hotJobs: boolean;
  jobDescriptionImages: boolean;
  maxCompanyAdminsAndRecruiters: number;
  maxDepartments: number;
  maxJobApplications: number;
  maxJobBrandings: number;
  maxLocations: number;
  maxRewards: number;
  paymentPlan: number;
  sharingTextsEnabled: boolean;
  statistics: boolean;
  webhooks: boolean;
}

export interface IFBSubscription {
  currency?: string;
  expirationDate?: string;
  paymentInterval?: string;
  plan: IFBPlan;
}

export interface IFBCompanyLanguage {
  defaultLocale: string;
  enabledLocales: string[];
  localeOverrides: IFBLocaleOverride[];
}

export interface IFBCompanyRegistrationPageBranding {
  backgroundImage: {
    id: string;
    href: string;
  };
  details: IFBMapWithLocaleKeys<IFBBrandedRegistrationText>;
  href: string;
  id: string;
  fetched?: boolean;
}

export interface IFBCompanyRegistrationPageBrandingUpdateBody {
  backgroundImage?: { id: string };
  details: IFBMapWithLocaleKeys<IFBBrandedRegistrationText>;
}

export interface IFBCompany {
  admin?: {
    locale?: string;
    timeZone?: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  domainName?: string;
  city?: string;
  companySize?: string;
  country?: string;
  currency?: string;
  cvRequired?: boolean;
  languages: IFBCompanyLanguage;
  logo?: IFBCompanyLogo;
  name?: string;
  id: string;
  postalCode?: string;
  registrationPageBranding?: IFBCompanyRegistrationPageBranding;
  rewardPaymentBehaviorCode?: string;
  street?: string;
  website?: string;
  subscription: { plan: IFBPlan };
}

export interface IFBOauthParams {
  accessToken?: string;
  error?: string;
  errorDescription?: string;
  expiresIn?: string;
  state?: string;
}

export interface IFBApiOauthParams {
  access_token: string;
  expires_in: number;
  company_id?: string;
  token_type?: string;
  scope?: string;
  jti?: string;
}

export interface PKCETokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
}

export interface IFBRegistrationLink extends IFBRegistrationLinkSource {}

export interface IFBLocation {
  id: string;
  href?: string;
  name: string;
  status: string;
}

export interface IFBDepartment {
  id: string;
  href?: string;
  name: string;
  status: string;
}

export interface IFBCurrency {
  code: string;
  symbol: string;
  name: string;
}

export interface IFBReward {
  details?: any;
  currency: string;
  id: string;
  name: string;
  quantityAvailable?: number;
  price: number;
  description?: string;
  imageId?: string;
  image?: {
    id: string;
    href: string;
  };
  monetaryValue?: {
    value?: number;
    currency?: string;
  };
  levelFrom?: number;
  version: number;
  quantityOrderable?: number;
  emailManager?: string;
  showQuantity?: boolean;
  showMonetaryValue?: boolean;
  status: string;
  requiresApproval?: boolean;
  translations: {
    [locale: string]: {
      name: string;
      description: string;
    };
  };
  segmentationProperty?: {
    segmentationId: string;
    segmentationValues: string[];
  };
}

export interface IFBMaterializedReward {
  autoApprove?: boolean;
  completedBy: IFBUserInfo;
  completedAt: string;
  currency: string;
  description: string;
  details: {
    jobApplication: { id: string; href: string };
    job: { id: string; href: string };
  };
  dueAt: string;
  id: string;
  recipient: { id: string; href: string };
  responsiblePerson: { id: string; href: string };
  status: EFBMaterializedRewardStatus;
  type: string;
  value: number;
}

export enum EFBMaterializedRewardStatus {
  OPEN = 'OPEN',
  PAID = 'PAID',
  DECLINED = 'DECLINED',
  CANCELLED = 'CANCELLED',
  REVOKED = 'REVOKED'
}

export interface IFBRewardResourceGetPageParams {
  limit?: number;
  offset?: number;
  companyId?: string;
  jobApplicationId?: string;
  recipientUserId?: string;
  responsiblePersonUserId?: string;
}

export interface IFBRewardShopSegmentationSettingsValue {
  segmentationValueId: string;
  active: boolean;
}

export interface IFBRewardShopSegmentationSettings {
  segmentationId: string;
  segmentationValues: IFBRewardShopSegmentationSettingsValue[];
}

export interface IFBRewardShopSettings {
  id?: string;
  coinCollectionEnabled: boolean;
  coinsInRewardPlansEnabled: boolean;
  displayOutOfStockItems: boolean;
  displayMaxBoughtItems: boolean;
  coinGeneratingActions: [string];
  version: number;
  visible: boolean;
  segmentationSettings: IFBRewardShopSegmentationSettings;
}

export interface IFBRewardShopUserBalance {
  id?: string;
  coins: number;
  level: number;
}

export interface IFBRewardShopUserPurchase {
  name: string;
  emailManager: string;
  image: {
    href: string;
    id: string;
  };
  price: number;
  productId: string;
  purchaseDate: string;
  purchaseId: string;
  status: string;
}

export interface IFBContactPerson {
  id?: string;
  firstName?: string;
  lastName?: string;
  href?: string;
  email?: string;
}

export interface IFBUserStatistics {
  followers: number;
  following: number;
  hires: number;
  jobApplications: number;
  jobViews: number;
  points: number;
  referrals: number;
  shares: number;
  topRatings: number;
  unreferredJobApplications: number;

  [prop: string]: number;
}

export interface IFBLeaderboardUser {
  companyId: string;
  userId: string;
  location: {
    id: string;
  };
  department: {
    id: string;
  };
  firstName: string;
  lastName: string;
  profile_picture: {
    id: string;
    href?: string;
  };
  points: number;
  allTimePoints: number;
  level?: number;
}

export interface IFBLeaderboardSettings {
  hideRecruiters: boolean;
  hideAdmins: boolean;
  period: string;
  restrictedVisibility?: IFBRestrictedVisibility;
  localCompetition?: {
    propertyId: string;
  };
}

export interface IFBRestrictedVisibility {
  propertyId: string;
  selectedValueIds: string[];
}

export interface IFBLevel {
  points: number;
  level: number;
}

export interface IFBActivity {
  object: { name: string; value: any };
  channel: string;
  company_id: string;
  user_id: string;
  actor: { name: string; value: any };
  job_id: string;
  user: any;
  type: string;
  show_cta: boolean;
  verb: string;
  time: string;
  timeElapsed: string;
  ready: boolean;
  shareType: string;
  network_type: string;
  level: number;
  job: any;
  job_application: any;
  closeReason: string;
  applicant: any;
  image?: string;
  incomplete: boolean;
  applicant_id: any;
  referral_id: any;
  recipient_id: any;
  rewardId: any;
}

export interface IFBApplication {
  applicant: IFBApplicant;
  applicantImage: { id: string; href: string };
  applicationDate: string;
  company: { id: string };
  closeReason?: string;
  channel?: string;
  feedbacks: IFBReferralFeedback[];
  files?: Array<{
    id: string;
    name: string;
  }>;
  formResponse: IFBCustomFormResponse;
  href: string;
  id: string;
  job: { id: string; title?: string; reward?: { id: string } };
  rating?: string;
  read: boolean;
  referrals: {
    negativeCount: number;
    neutralCount: number;
    referralCount: number;
  };
  referrer: { id: string };
  rewardPaid: boolean;
  status: string;
  firstDayOfWork?: string;
}

export interface IFBJobApplicationSettings {
  deleteClosedJobApplicationAfterDays: number;
  deleteClosedJobApplications: boolean;
  deleteHiredJobApplicationAfterDays: number;
  deleteHiredJobApplications: boolean;
  deletePendingJobApplicationAfterDays: number;
  deletePendingJobApplications: boolean;
  recruiterRatingEnabled: boolean;
}

export interface IFBReferralTenantSettings {
  talentscoutFeedbackEnabled: boolean;
  personalReferralLinkEnabled?: boolean;
}

export interface IFBJobApplicationDeletionSettings extends Omit<IFBJobApplicationSettings, 'recruiterRatingEnabled'> {}

export interface IFBApplicationFileInfo {
  href: string;
  id: string;
  name: string;
  contentType: string;
  size: number;
  url: string;
}

export interface IFBApplicationReward {
  dueAt: string;
  description: string;
  value: number;
  currency: string;
}

export interface IFBJobCompany {
  city?: string;
  companyCategory?: string;
  companySize?: string;
  country?: string;
  currency?: string;
  cvRequired?: boolean;
  domainName?: string;
  href?: string;
  id: string;
  logo?: { id: string; href: string };
  name?: string;
  postalCode?: string;
  rewardPaymentBehaviorCode?: string;
  status?: string;
  street?: string;
  website?: string;
}

export interface IFBJobCounts {
  published: number;
  closed: number;
  archived: number;
  draft: number;
}

export interface IFBJob {
  company?: IFBJobCompany;
  contactPerson: IFBUserInfo;
  creationDate: string;
  department: IFBDepartment;
  description?: string;
  endDate?: string;
  expired?: boolean;
  expiring?: boolean;
  hot: boolean;
  href?: string;
  id: string;
  isFavorite?: boolean;
  jobBranding?: { id: string; href: string };
  jobPostingUrl?: string;
  image?: { href: string };
  location: IFBLocation;
  referenceNumber?: string;
  responsiblePersons?: Array<{ id: string; href: string }>;
  responsiblePerson?: string;
  reward: IFBRewardPlan;
  shareLink?: { href: string };
  statistics?: { href: string };
  status: string;
  title: string;
  unlimitedDuration?: boolean;
}

export interface IFBJobAi {
  id: string;
  hot: boolean;
  locationId: string;
  departmentId: string;
  rewardId: string;
  title: string;
  creationDate: string;
}

export interface IFBJobPayload {
  company: IFBJobCompany;
  contactPerson: Pick<IFBUserInfo, 'id'>;
  creationDate: string;
  department: Pick<IFBDepartment, 'id'>;
  description: string;
  endDate?: string | Date;
  expired?: boolean;
  expiring?: boolean;
  hot: boolean;
  id: string;
  jobBranding: { id: string };
  image: { href: string };
  location: Pick<IFBLocation, 'id'>;
  referenceNumber: string;
  responsiblePersons: Pick<IFBUserInfo, 'id'>[];
  responsiblePerson: string;
  reward: Pick<IFBRewardPlan, 'id'>;
  shareLink: { href: string };
  statistics: { href: string };
  title: string;
}

export interface IFBJobResourceGetPageParams {
  [param: string]: string | number | boolean | string[];
  brandings?: string;
  contact?: string;
  department?: string;
  endDateBefore?: string;
  fields?: string;
  hot?: boolean;
  limit?: number;
  location?: string;
  offset?: number;
  query?: string;
  referenceNumber?: string;
  sort?: string;
  status?: string;
}

export interface IFBJobStatistics {
  closed: number;
  hires: number;
  jobApplications: number;
  jobViews: number;
  referrals: number;
  shares: number;
  topRatings: number;

  [key: string]: number;
}

export interface IFBJobOverallStatistics {
  [key: string]: number;

  applications: number;
  aRated: number;
  bRated: number;
  closed: number;
  cRated: number;
  directShares: number;
  directViews: number;
  emailShares: number;
  emailViews: number;
  facebookShares: number;
  facebookViews: number;
  hired: number;
  jobsPublished: number;
  linkedInShares: number;
  linkedInViews: number;
  referrals: number;
  twitterShares: number;
  twitterViews: number;
  whatsappShares: number;
  whatsappViews: number;
  xingShares: number;
  xingViews: number;
}

export interface IFBLegalTermsLocales {
  available: string[];
  primary: string;
}

export interface IFBLegalTerm {
  [document: string]: any;

  imprint: string;
  locale: string;
  privacyPolicy: string;
  termsOfUse: string;
  updatedAt: string;
}

export interface IFBPasswordPolicy {
  attempts_until_lockout: number;
  characteristics: string[];
  minimum_length: number;
  name?: string;
  reuse_after_passwords?: number;
  $$values?: {
    mixedCase?: boolean;
    characteristic?: string;
  };
}

export interface IFBShareSettings {
  channels: { channel: string }[];
}

export interface IFBWebhookEventPage extends IFBDataPage {
  items: IFBWebhookEvent[];
}

export interface IFBWebhookEvent {
  body?: any;
  dateTime: string;
  destination: string;
  destinationName?: string;
  eventType: string;
  id: string;
  status?: number;
}

export interface IFBWebhook {
  eventTypes: string[];
  id?: string;
  name?: string;
  status: string;
  url?: string;
  authentication?: {
    type: string;
    username?: string;
    password?: string;
    clientId?: string;
    clientSecret?: string;
    tokenUrl?: string;
  };
}

export interface IFBNotificationSettings {
  digestFrequency: 'HOURLY' | 'DAILY' | 'WEEKLY';
  dueReward: boolean;
  jobApplicationUpdatesViaEmail: boolean;
  jobDigest: boolean;
  mobilePush: boolean;
  publishedHotJobEmail: boolean;
  publishedJobAtFavoriteDepartmentEmail: boolean;
  publishedJobAtFavoriteLocationEmail: boolean;
  referralReceived: boolean;
  referralReminder: boolean;
  referrerFeedback: boolean;
}

export interface IFBNotification {
  jobId: string;
  type: string;
}

export interface IFBGreenhouseConnectedUser {
  email: string;
  firstName: string;
  lastName: string;
}

export interface IFBGreenhouseServiceData {
  connectedUser?: IFBGreenhouseConnectedUser;
  enabled: boolean;
  harvestApiKey?: string;
  harvestClientId?: string;
}

export interface IFBSmartRecruitersServiceData {
  enabled: boolean;
  grantType?: 'client_credentials';
  clientId?: string;
  clientSecret?: string;
}

export interface IFBSlackServiceData {
  channel?: string | undefined;
  companyId?: string;
  locale?: string;
  enabled: boolean;
  teamName: string;
}

export interface IFBShareProvider {
  key: string;
  name: string;
  isEditing: boolean;
}

export interface IFBCompanyBranding {
  company?: { id: string; href: string };
  createdAt?: string;
  createdAtFormatted?: string;
  href?: string;
  id: string;
  isDefault: boolean;
  jobHeader: {
    id?: string;
    href?: string;
  };
  jobHeaderColor: string;
  jobBackgroundColor: string;
  linkedInShareLogo?: { id: string; href: string };
  name: string;
  shareDescription?: string;
  shareLogo: {
    id?: string;
    href?: string;
  };
  shareLogoWide: {
    id?: string;
    href?: string;
  };
  sharingTexts: IFBSharingTexts;
  updatedAt?: string;
  updatedAtFormatted?: string;
}

// {object{ facebook: {en_US: ..., es_ES: ... } }} map of texts for locales
export interface IFBSharingTexts {
  [provider: string]: IFBMapWithLocaleKeys<string>;
}

// Covers structures like {en_US: ..., es_ES: ... } with the variable value
export interface IFBMapWithLocaleKeys<T> {
  [locale: string]: T;
}

export interface IFBBrandedRegistrationText {
  headingText: string;
  welcomeText: string;
}

export interface IFBLanguage {
  locale: string;
  name?: string;
}

export interface IFBLanguageWithName extends IFBLanguage {
  name?: string;
}

export interface IFBRewardPolicy {
  description: string;
  id: string;
}

export interface IFBRewardPolicyWithLocale extends IFBRewardPolicy {
  locale: string;
}

export interface IFBRewardPolicyLocales {
  available: string[];
  primary: string;
}

export interface IFBRewardItemDetails {
  autoApprove: boolean;
  paymentDate: string;
  type: string | null;
  description?: string;
  value?: number;
  coins?: number;
  currency?: string;
  rewardSegmentPropertyValueId?: string;
  rewardSegmentIsAll?: boolean;
}

export interface IFBRewardPlan {
  currency?: string;
  type: string;
  external: boolean;
  details: { items: IFBRewardItemDetails[] };
  id?: string;
  name?: string;
  href?: string;
  description?: string;
  rewardSegmentPropertyId?: string;
  segmentationEnabled?: boolean;
}

export interface IFBRewardPaymentBehavior {
  code: string;
  id: string;
}

export interface IFBFile extends File {
  name: string;
  size: number;
  lastModified: number;
  lastModifiedDate?: Date;
  type: string;
  webkitRelativePath: string;
  'X-Transformations'?: string;
}

export interface IFBFileUploadApiResponse {
  contentType: string;
  id: string;
  name: string;
  size: number;
}

export interface IFBPagedResponseHttp<T> {
  first: {
    href: string;
  };
  href: string;
  items: T[];
  last: {
    href: string;
  };
  limit: number;
  next: {
    href: string;
  };
  offset: number;
  total: number;
}

export interface IFBJobsPage extends IFBPagedResponseHttp<IFBJob> {}
export interface IFBJobsAiPage extends IFBPagedResponseHttp<IFBJobAi> {}

export interface IFBRewardingPlansPage extends IFBPagedResponseHttp<IFBRewardPlan> {}

export interface IFBReferralFeedback {
  connection: string;
  reason: string;
  referralType: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
}

export enum EFBMyReferralStatus {
  CLOSED = 'CLOSED',
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  OPEN = 'OPEN',
  NEW = 'NEW'
}

export interface IFBMyReferral {
  channel: 'DIRECTSHARE' | 'DIRECTLINK';
  createdAt: string;
  email: string;
  firstName: string;
  href: string;
  id: string;
  jobId: string;
  lastName: string;
  status: EFBMyReferralStatus;
  talentscoutId: string;
  tenantId: string;

  // Fields that may be populated on FE
  feedback?: IFBReferralFeedback;
  job?: IFBJob;
}

export interface IFBTimezone {
  countryCode: string;
  suffix: string;
  zoneName: string;
}

export interface IFBUserInterest {
  id?: string;
  interestType: EFBInterestTypes;
  value: string;
}

export enum EFBInterestTypes {
  LOCATION = 'LOCATION',
  DEPARTMENT = 'DEPARTMENT',
  HOT_JOB = 'HOT_JOB'
}

export interface IFBReason {
  code: string;
}

export interface IFBHtmlInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

export interface IFBReferralResponse {
  followUpUrl: string;
  id: string;
  referralToken: string;
}

export interface IFBTokenInfo {
  candidateInfo: IFBReferralInfo;
  consentRequirements: IFBConsentForm[];
  job: { jobId: string };
  referral?: {
    channel: string;
    referralId: string;
  };
  share: { shareId: string };
  talentscout: {
    companyId: string;
    id: string;
    href: string;
    firstName: string;
    lastName: string;
    profilePicture: { href: string };
    meta: {
      provisioning: {
        provisionedFields: any[];
      };
    };
    talentscoutId?: string;
  };
  personalReferralToken?: string;
}

export interface IFBTokenInfoV2 {
  consentRequirements: IFBConsentForm[];
  channel: string;
  talentscout: {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture: string;
  };
  personalReferralToken?: string;
}

export interface IFBCreateReferralPayload {
  consentRequirements: IFBContentFormFieldResponse[];
  email: string;
  firstName: string;
  lastName: string;
  shareToken?: string;
}

export interface IFBCreateReferralManuallyPayload {
  candidateInfo: IFBReferralInfo;
  jobId: string;
  talentscoutId: string;
  startDate: string;
  payReward: boolean;
}

export interface IFBResume {
  id: string;
  name: string;
}

export interface IFBApplicant {
  acceptedTerms: boolean;
  applicantImage: string | undefined;
  company?: { id: string; href: string };
  doNotPayReward?: boolean;
  email: string;
  files?: Array<{
    id: string;
    name: string;
  }>;
  firstDayOfWork?: dayjs.Dayjs;
  firstName: string;
  id?: string;
  jobId?: string;
  jti?: string;
  jwt: string;
  lastName: string;
  name?: string;
  noRewardReason?: string;
  phoneNumber: string;
  referralId?: string;
  resumes: IFBResume[];
  url: string;
}

export interface IFBReferralApplication {
  image?: { id: string };
  job: { id: string };
  candidate: Partial<{
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    socialNetworkUrl?: string;
  }>;
  acceptedTerms?: boolean;
  files?: { id: string }[];
  formResponse?: IFBCustomFormResponse;
  $$recaptchaToken?: string;
  source?: {
    name: string;
    data: {
      identifier: string;
      channel?: string;
      user?: { id: string };
    };
  };
}

export interface IFBReferrer {
  companyId: string;
  firstName: string;
  href: string;
  id: string;
  lastName: string;
  meta: {
    provisioning: {
      provisionedFields: any[];
    };
  };
  profilePicture?: {
    href?: string;
    id?: string;
  };
}

export interface IFBJobApplicationCloseReason {
  code: string;
}

export interface IFBRewardPolicies extends IFBMapWithLocaleKeys<IFBRewardPolicyWithLocale> {}

export interface IFBClientKey {
  id: string;
  secret?: string;
  ipWhitelist?: string[];
}

export interface IFBImgDimensions {
  width: number;
  height: number;
}

export interface IFBCompanyFeatureConf {
  active: boolean;
  description: string;
  key: string;
  name: string;
}

export interface IFBNumberOfDaysRestriction {
  min: number;
  max: number;
}

export interface IFBRestoreScrollPosition {
  stateTo: string;
  stateFrom: string;
  fetchLimit: number;
  scrollTop: number;
}

export interface ButtonGroupOption {
  title: string;
  value: string;
}

export interface IFBShareInfo {
  job: {
    id: string;
    title: string;
    description: string;
    location: string;
  };
  talentscout: {
    firstName: string;
    lastName: string;
    profilePicture: {
      href: string;
    };
  };
  candidate?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  consentRequirements: {
    name: string;
    translations: {
      [key: string]: string;
    };
  }[];
}

export interface IFBAcceptReferral {
  referralToken: string;
  referralId: string;
  followUpUrl?: string;
}

export interface IFBReferralInfo {
  firstName: string;
  lastName: string;
  email: string;
}

export enum ESharingChannel {
  FACEBOOK = 'FACEBOOK',
  LINKEDIN = 'LINKEDIN',
  TWITTER = 'TWITTER',
  XING = 'XING',
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  DIRECTLINK = 'DIRECTLINK',
  WECHAT = 'WECHAT',
  NATIVE = 'NATIVE'
}
